import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';

import { showMsg } from '../../services/library';
import { TRANSITION_LIST, VOICE_LIST } from '../../constants/arrayListConst';
import VideoService from '../../services/videoService';

const REQ_VIDEOGENERATION = 'videogeneration/';
export const CUSTOM_VIDEO_FONT_STYLE_NAME = 'dynamic-video-font-family-style';
export const CUSTOM_SELECTED_FONT_STYLE_NAME = 'dynamic-selected-font-family-style';

export const fetchDealerInfoReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}fetchDealerInfoReq`,

  async ({ dealer_id }, { rejectWithValue }) => {
    try {
      const response = await VideoService.getDealerInfo({
        dealer_id
      });
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const saveVideoSettingsReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}saveVideoSettingsReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.saveVideoSettings(data);
      if (response?.status) {
        showMsg(response.message, 'success');
        return;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const saveSelectedMusicReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}saveSelectedMusicReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.saveSelectedMusic(data);
      if (response?.status) {
        showMsg(response.message, 'success');
        return;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const uploadMusicFileReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}uploadMusicFileReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.uploadMusicFile(data);
      if (response?.status) {
        showMsg('Music track uploaded.', 'success');
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const deleteMusicReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}deleteMusicReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.deleteMusicFile(data);
      if (response?.status) {
        showMsg(response?.message, 'success');
        return data || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const uploadVideoFileReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}uploadVideoFileReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.uploadVideoFile(data);
      if (response?.status) {
        showMsg('Video Uploaded.', 'success');
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const deleteVideoFileReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}deleteVideoFileReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.deleteVideoFile(data);
      if (response?.status) {
        showMsg('Video Deleted.', 'success');
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const convertTextToSpeechReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}convertTextToSpeechReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.convertTextToSpeech(data);
      if (response?.status) {
        showMsg('Voice(s) and text are saved.', 'success');
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const deleteSpeechReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}deleteSpeechReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.deleteSpeech(data);
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const saveImageSettingsReq = createAsyncThunk(
  `${REQ_VIDEOGENERATION}saveImageSettingsReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await VideoService.saveImageSettings(data);
      if (response?.status) {
        showMsg('Image settings saved.', 'success');
        return response?.data || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

const videoGenerationSlice = createSlice({
  name: 'videoGeneration',
  initialState: {
    shotDetails: [],
    dataLoaded: false,
    language: { label: 'English', value: 'en-US' },
    voiceOver: false,
    voiceNamesList: [],
    selectedVoiceNames: [{ label: 'Neural2-A (Male)', value: 'en-US-Neural2-A' }],
    introDuration: 0,
    outroDuration: 0,
    transition: { label: 'Not Set', value: '' },
    introVideo: { url: '', status: false, value: 30 },
    outroVideo: { url: '', status: false, value: 30 },
    videoOrderMissing: false,
    isVideoSettingMissing: false,
    speech: [],
    isSpeechLoading: false,
    introText: '',
    outroText: '',
    isSpeechSettingMissing: false,
    musicLoading: { status: false, value: 30 },
    toggleMusicModal: false,
    musicList: [],
    selectedMusicList: [],
    isMusicSettingMissing: false,
    lower23rdImageDetail: null,
    imageDuration: 2,
    textDelay: 2,
    toggleImageModal: false,
    imgDimensions: null,
    toggleFontModal: false,
    fontList: [],
    isFontLoading: true,
    toggleVoiceDemoModal: false,
    phrase: '',
    replacePhrase: '',
    dealerAiPrompt: '',
    phraseList: [],
    isPhraseLoading: true,
    editPhraseValue: '',
    editReplacePhraseValue: ''
  },
  reducers: {
    handleShotDetails: {
      reducer(state, action) {
        state.shotDetails = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDataLoaded: {
      reducer(state, action) {
        state.dataLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleLanguage: {
      reducer(state, action) {
        state.language = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVoiceOver: {
      reducer(state, action) {
        state.voiceOver = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVoiceNamesList: {
      reducer(state, action) {
        state.voiceNamesList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSelectedVoiceNames: {
      reducer(state, action) {
        state.selectedVoiceNames = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIntroDuration: {
      reducer(state, action) {
        state.introDuration = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleOutroDuration: {
      reducer(state, action) {
        state.outroDuration = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTransition: {
      reducer(state, action) {
        state.transition = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIntroVideo: {
      reducer(state, action) {
        state.introVideo = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleOutroVideo: {
      reducer(state, action) {
        state.outroVideo = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsVideoSettingMissing: {
      reducer(state, action) {
        state.isVideoSettingMissing = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSpeech: {
      reducer(state, action) {
        state.speech = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsSpeechLoading: {
      reducer(state, action) {
        state.isSpeechLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIntroText: {
      reducer(state, action) {
        state.introText = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleOutroText: {
      reducer(state, action) {
        state.outroText = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsSpeechSettingMissing: {
      reducer(state, action) {
        state.isSpeechSettingMissing = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleMusicLoading: {
      reducer(state, action) {
        state.musicLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleToggleMusicModal: {
      reducer(state, action) {
        state.toggleMusicModal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleMusicList: {
      reducer(state, action) {
        state.musicList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSelectedMusicList: {
      reducer(state, action) {
        state.selectedMusicList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsMusicSettingMissing: {
      reducer(state, action) {
        state.isMusicSettingMissing = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleLower23rdImageDetail: {
      reducer(state, action) {
        state.lower23rdImageDetail = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleImageDuration: {
      reducer(state, action) {
        state.imageDuration = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTextDelay: {
      reducer(state, action) {
        state.textDelay = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleToggleImageModal: {
      reducer(state, action) {
        state.toggleImageModal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleImgDimensions: {
      reducer(state, action) {
        state.imgDimensions = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleToggleFontModal: {
      reducer(state, action) {
        state.toggleFontModal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFontList: {
      reducer(state, action) {
        state.fontList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsFontLoading: {
      reducer(state, action) {
        state.isFontLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleToggleVoiceDemoModal: {
      reducer(state, action) {
        state.toggleVoiceDemoModal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePhrase: {
      reducer(state, action) {
        state.phrase = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleReplacePhrase: {
      reducer(state, action) {
        state.replacePhrase = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePhraseList: {
      reducer(state, action) {
        state.phraseList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsPhraseLoading: {
      reducer(state, action) {
        state.isPhraseLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleEditPhraseValue: {
      reducer(state, action) {
        state.editPhraseValue = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleEditReplacePhraseValue: {
      reducer(state, action) {
        state.editReplacePhraseValue = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    }
  },
  extraReducers: builder => {
    builder
      //#region fetchDealerInfoReq
      .addCase(fetchDealerInfoReq.pending, state => {
        state.dataLoaded = false;
      })
      .addCase(fetchDealerInfoReq.fulfilled, (state, action) => {
        const data = action.payload;
        let musicList = data?.musicList.map(item => {
          let checked = false;
          if (data?.dealer_music && data?.dealer_music.split(',').includes(item.vm_id.toString())) {
            checked = true;
          }
          return {
            id: item.vm_id,
            filename: item.vm_filename,
            url: item.vm_url,
            checked
          };
        });

        const speech =
          data?.dealer_speech && data?.dealer_speech?.length > 0
            ? data?.dealer_speech?.map(item => {
                let index = VOICE_LIST.findIndex(i => i.label === item.lang);
                let type = VOICE_LIST[index]?.voiceNames.find(d => d.value === item.type)?.label;
                return {
                  ...item,
                  type,
                  loading: false
                };
              })
            : [];

        state.dataLoaded = true;
        state.dealerAiPrompt = data?.dealer_aisummary_prompt || '';
        state.shotDetails = data?.shotDetails.map((x, i) => {
          return { ...x, itemno: i + 1 };
        });
        state.introDuration = data?.dealer_intro_duration || 0;
        state.outroDuration = data?.dealer_outro_duration || 0;
        state.transition = TRANSITION_LIST.find(
          tr => tr.value.toLowerCase() === (data?.dealer_video_transition || '').toLowerCase()
        );

        state.introVideo = {
          ...state.introVideo,
          url: data?.dealer_intro_path
        };
        state.outroVideo = {
          ...state.outroVideo,
          url: data?.dealer_outro_path
        };
        state.isVideoSettingMissing = data?.videoOrderMissing;

        state.introText = data?.introText || '';
        state.outroText = data?.outroText || '';
        state.speech = speech;
        state.isSpeechSettingMissing =
          data?.introText?.length > 0 && data?.outroText.length > 0 && speech.length > 0 ? false : true;
        state.musicList = musicList;
        state.isMusicSettingMissing = state.selectedMusicList.length > 0 ? false : true;
        state.fontList =
          data?.fontList?.map(d => ({
            ...d,
            label: d.vf_filename,
            value: d.vf_id
          })) || [];
        state.isFontLoading = false;
        state.lower23rdImageDetail = data?.lower23rdImageDetail || null;
        state.imageDuration = data?.dealer_image_duration || 2;
        state.textDelay = data?.dealer_text_delay || 2;
        state.voiceOver = data?.dealer_voice_over || 0;
        state.phraseList = data?.phraseList || 0;
        state.isPhraseLoading = false;
      })
      .addCase(fetchDealerInfoReq.rejected, state => {
        state.dataLoaded = true;
      })
      // #endregion

      //#region saveVideoSettingsReq
      .addCase(saveVideoSettingsReq.fulfilled, state => {
        state.isVideoSettingMissing = false;
      })

      // #endregion
      //#region saveSelectedMusicReq
      .addCase(saveSelectedMusicReq.fulfilled, state => {
        state.isMusicSettingMissing = state.selectedMusicList.length > 0 ? false : true;
      })
      // #endregion

      //#region uploadMusicFileReq
      .addCase(uploadMusicFileReq.fulfilled, (state, action) => {
        const data = action.payload;
        let newData = data?.insertData.map(item => {
          return {
            id: item.vm_id,
            filename: item.vm_filename,
            url: item.vm_url,
            checked: false
          };
        });
        state.musicList = [...current(state).musicList, ...newData];
      })
      .addCase(uploadMusicFileReq.rejected, state => {
        state.musicLoading = { status: false, value: 30 };
      })
      // #endregion

      //#region deleteMusicReq
      .addCase(deleteMusicReq.fulfilled, (state, action) => {
        const data = action.payload;
        state.musicList = current(state).musicList.filter(item => item.id !== data.vm_id);
      })
      .addCase(deleteMusicReq.rejected, state => {
        state.musicList = current(state).musicList.map(item => ({ ...item, loading: false }));
      })

      // #region convertTextToSpeechReq
      .addCase(convertTextToSpeechReq.fulfilled, (state, action) => {
        const data = action.payload;
        const speech = data?.speech?.list
          ? data?.speech?.list.map(item => {
              let index = VOICE_LIST.findIndex(i => i.label === item.lang);
              let type = VOICE_LIST[index]?.voiceNames?.find(d => d.value === item.type)?.label;
              return { ...item, type, loading: false };
            })
          : [];
        state.speech = speech;
        state.isSpeechSettingMissing =
          state?.introText?.length > 0 && state?.outroText?.length > 0 && speech?.length > 0 ? false : true;
      })
      // #endregion

      // #region deleteSpeechReq
      .addCase(deleteSpeechReq.fulfilled, (state, action) => {
        const data = action.payload;
        let updatedArray = data?.updatedArray?.list?.length ? data?.updatedArray?.list : [];
        const speech = updatedArray.map(item => {
          let index = VOICE_LIST.findIndex(i => i.label === item.lang);
          let type = VOICE_LIST[index]?.voiceNames.find(d => d.value === item.type).label;
          return { ...item, type, loading: false };
        });
        state.speech = speech;
        state.isSpeechSettingMissing = speech.length > 0 ? false : true;
      });
    // #endregion
  }
});

export const {
  handleShotDetails,
  handleLanguage,
  handleVoiceOver,
  handleVoiceNamesList,
  handleSelectedVoiceNames,
  handleIntroDuration,
  handleOutroDuration,
  handleTransition,
  handleIntroVideo,
  handleOutroVideo,
  handleIsVideoSettingMissing,
  handleSpeech,
  handleIsSpeechLoading,
  handleIntroText,
  handleOutroText,
  handleIsSpeechSettingMissing,
  handleMusicLoading,
  handleToggleMusicModal,
  handleMusicList,
  handleSelectedMusicList,
  handleIsMusicSettingMissing,
  handleLower23rdImageDetail,
  handleImageDuration,
  handleTextDelay,
  handleToggleImageModal,
  handleImgDimensions,
  handleToggleFontModal,
  handleFontList,
  handleIsFontLoading,
  handleToggleVoiceDemoModal,
  handlePhrase,
  handleReplacePhrase,
  handlePhraseList,
  handleIsPhraseLoading,
  handleEditPhraseValue,
  handleEditReplacePhraseValue
} = videoGenerationSlice.actions;
export default videoGenerationSlice.reducer;
